import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNOK, formatHours } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  ExternalCostSummary,
  InvoicedSummary,
  PersonHoursRow,
  PhaseHoursRow,
} from "@/lib/project-report-calc";
import type { UserLookup } from "@/hooks/use-users";

const PHASE_NB: Record<string, string> = {
  preprod: "Preproduksjon",
  production: "Produksjon",
  postprod: "Postproduksjon",
  travel: "Reise",
  reise: "Reise",
  ukjent: "Uten fase",
};

interface Props {
  phaseHours: PhaseHoursRow[];
  personHours: PersonHoursRow[];
  externalCosts: ExternalCostSummary;
  invoiced: InvoicedSummary;
  bfEstimatedKr: number;
  bfActualKr: number;
  billingContributionHours: number;
  userMap: Map<string, UserLookup>;
}

const devClass = (n: number) => (n > 0 ? "text-red-600" : n < 0 ? "text-emerald-700" : "text-muted-foreground");

/**
 * Styringsdelen av den interne prosjektrapporten (VIB-1068): timer per
 * fase/person, estimert vs. påløpt ekstern kost, BF + dekningsgrad,
 * fakturert vs. gjenstående. READ-ONLY — alle tall avledes av kanoniske kilder.
 */
export function ReportStyringSection({
  phaseHours,
  personHours,
  externalCosts,
  invoiced,
  bfEstimatedKr,
  bfActualKr,
  billingContributionHours,
  userMap,
}: Props) {
  const dekningsgrad =
    invoiced.contractKr > 0 ? Math.round((bfActualKr / invoiced.contractKr) * 1000) / 10 : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">GEEK estimert / reell</p>
            <p className="text-lg font-bold tabular-nums">
              {formatNOK(bfEstimatedKr)} / {formatNOK(bfActualKr)}
            </p>
            <p className="text-xs text-muted-foreground">
              Dekningsgrad: {dekningsgrad != null ? `${dekningsgrad} %` : "—"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Fakturert / gjenstående</p>
            <p className="text-lg font-bold tabular-nums">{formatNOK(invoiced.invoicedKr)}</p>
            <p className="text-xs text-muted-foreground">
              Gjenstår {formatNOK(invoiced.remainingKr)} av {formatNOK(invoiced.contractKr)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Ekstern kost (påløpt / estimert)</p>
            <p className="text-lg font-bold tabular-nums">
              {formatNOK(externalCosts.totalAccruedKr)} / {formatNOK(externalCosts.totalEstimatedKr)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Faktureringsgrad-bidrag</p>
            <p className="text-lg font-bold tabular-nums">{formatHours(billingContributionHours)}</p>
            <p className="text-xs text-muted-foreground">Fullførte/godkjente tildelte timer</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Timer per fase (fremdrift)</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2">Fase</th>
                <th className="text-right py-2">Budsjettert</th>
                <th className="text-right py-2">Brukt</th>
                <th className="text-right py-2">Avvik</th>
              </tr>
            </thead>
            <tbody>
              {phaseHours.map((r) => (
                <tr key={r.phase} className="border-b border-border/50 last:border-0">
                  <td className="py-2">{PHASE_NB[r.phase] ?? r.phase}</td>
                  <td className="py-2 text-right tabular-nums">{formatHours(r.budgetedHours)}</td>
                  <td className="py-2 text-right tabular-nums">{formatHours(r.loggedHours)}</td>
                  <td className={cn("py-2 text-right tabular-nums", devClass(r.deviationHours))}>
                    {r.deviationHours > 0 ? "+" : ""}
                    {formatHours(r.deviationHours)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Timer per person</CardTitle>
          </CardHeader>
          <CardContent>
            {personHours.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen tildelinger.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-2">Person</th>
                    <th className="text-right py-2">Tildelt</th>
                    <th className="text-right py-2">Logget</th>
                  </tr>
                </thead>
                <tbody>
                  {personHours.map((r) => (
                    <tr key={r.userId} className="border-b border-border/50 last:border-0">
                      <td className="py-2">{userMap.get(r.userId)?.full_name ?? "Ukjent"}</td>
                      <td className="py-2 text-right tabular-nums">{formatHours(r.assignedHours)}</td>
                      <td className="py-2 text-right tabular-nums">{formatHours(r.loggedHours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ekstern kost per linje (estimert vs. påløpt)</CardTitle>
          </CardHeader>
          <CardContent>
            {externalCosts.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen eksterne kostnadslinjer.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-2">Linje</th>
                    <th className="text-right py-2">Estimert</th>
                    <th className="text-right py-2">Påløpt</th>
                    <th className="text-right py-2">Avvik</th>
                  </tr>
                </thead>
                <tbody>
                  {externalCosts.rows.map((r) => (
                    <tr key={r.lineId} className="border-b border-border/50 last:border-0">
                      <td className="py-2">{r.description}</td>
                      <td className="py-2 text-right tabular-nums">{formatNOK(r.estimatedKr)}</td>
                      <td className="py-2 text-right tabular-nums">{formatNOK(r.accruedKr)}</td>
                      <td className={cn("py-2 text-right tabular-nums", devClass(r.deviationKr))}>
                        {r.deviationKr > 0 ? "+" : ""}
                        {formatNOK(r.deviationKr)}
                        {r.deviationPct != null && (
                          <span className="text-xs text-muted-foreground ml-1">({r.deviationPct} %)</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
